import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { parseCsv, cleanCsvField } from '../../utils/csv';
import { canWrite } from '../../utils/roles';

function normalize(str) {
  return str?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') || '';
}

// CSV de Odoo (x_lugares): "Created by","Descripción","Ubicación"
function parseLugaresCsv(text) {
  const [, ...dataRows] = parseCsv(text);
  return dataRows.map(([, name, address]) => ({
    name: cleanCsvField(name),
    address: cleanCsvField(address),
  })).filter(r => r.name);
}

function LugarModal({ lugar, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: lugar?.name || '',
    short_name: lugar?.short_name || '',
    address: lugar?.address || '',
    google_maps_url: lugar?.google_maps_url || '',
    tolerancia_metros: lugar?.tolerancia_metros ?? 500,
    genera_incidencia: Boolean(lugar?.genera_incidencia),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mapsUrlChanged = form.google_maps_url.trim() !== String(lugar?.google_maps_url || '').trim();

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setChecked = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es requerido'); return; }
    setSaving(true);
    setError('');
    try {
      const { data } = lugar
        ? await api.put(`/lugares/${lugar.id}`, form)
        : await api.post('/lugares', form);
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{lugar ? 'Editar lugar' : 'Nuevo lugar'}</h3>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nombre *</label>
              <input className="form-input" value={form.name} onChange={set('name')} placeholder="Nombre del lugar" autoFocus spellCheck={false} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nombre corto</label>
              <input className="form-input" value={form.short_name} onChange={set('short_name')} placeholder="Referencia breve para operación y RRHH" spellCheck={false} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Dirección</label>
              <input className="form-input" value={form.address} onChange={set('address')} placeholder="Dirección u ubicación (opcional)" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Google Maps</label>
              <input className="form-input" value={form.google_maps_url} onChange={set('google_maps_url')} placeholder="URL de Google Maps (opcional)" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10, alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Coordenadas</label>
                <div className="form-input" style={{ display: 'flex', alignItems: 'center', color: 'var(--text-dim)' }}>
                  {!mapsUrlChanged && lugar?.latitud != null && lugar?.longitud != null
                    ? `${Number(lugar.latitud).toFixed(6)}, ${Number(lugar.longitud).toFixed(6)}`
                    : form.google_maps_url.trim() ? 'Se actualizan al guardar' : 'Sin coordenadas'}
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Tolerancia</label>
                <input className="form-input" type="number" min="1" step="1" value={form.tolerancia_metros} onChange={set('tolerancia_metros')} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-dim)', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.genera_incidencia} onChange={setChecked('genera_incidencia')} />
              Las marcas en este lugar generan observación
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : lugar ? 'Guardar cambios' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EspacioAdder({ lugarId, onAdded }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/lugares/${lugarId}/espacios`, { name: trimmed });
      onAdded(data);
      setName('');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al agregar espacio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: 6, marginTop: 8 }}>
      <input
        className="form-input"
        style={{ fontSize: 13, flex: 1, maxWidth: 220 }}
        placeholder="Agregar sub-lugar (espacio)…"
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <button type="submit" className="btn btn-ghost" style={{ fontSize: 12 }} disabled={saving || !name.trim()}>
        + Agregar
      </button>
    </form>
  );
}

function LugarRow({ lugar, isAdmin, expanded, onToggleExpand, onEdit, onToggleActive, onEspaciosChange }) {
  const draggedId = useRef(null);

  const removeEspacio = async espacioId => {
    if (!window.confirm('¿Eliminar este espacio?')) return;
    try {
      await api.delete(`/lugares/espacios/${espacioId}`);
      onEspaciosChange(lugar.id, lugar.espacios.filter(e => e.id !== espacioId));
    } catch {}
  };

  const addEspacio = espacio => {
    onEspaciosChange(lugar.id, [...lugar.espacios, espacio]);
  };

  const reorderEspacios = async targetId => {
    const fromId = draggedId.current;
    draggedId.current = null;
    if (!fromId || fromId === targetId) return;
    const order = lugar.espacios.map(e => e.id);
    const fromIndex = order.indexOf(fromId);
    const toIndex = order.indexOf(targetId);
    if (fromIndex === -1 || toIndex === -1) return;
    const newOrder = [...order];
    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, fromId);
    const byId = Object.fromEntries(lugar.espacios.map(e => [e.id, e]));
    onEspaciosChange(lugar.id, newOrder.map(id => byId[id]));
    try {
      await api.put(`/lugares/${lugar.id}/espacios/reorder`, { order: newOrder });
    } catch (e) {
      onEspaciosChange(lugar.id, lugar.espacios);
    }
  };

  const metaParts = [];
  if (lugar.address) metaParts.push(lugar.address);
  if (lugar.latitud !== null && lugar.latitud !== undefined && lugar.longitud !== null && lugar.longitud !== undefined) {
    metaParts.push(`geocerca ${lugar.tolerancia_metros || 500} m${lugar.genera_incidencia ? ' · observa' : ''}`);
  }
  if (lugar.espacios?.length > 0) metaParts.push(`${lugar.espacios.length} espacio${lugar.espacios.length === 1 ? '' : 's'}`);

  return (
    <div>
      <div
        className={`vlist-row${lugar.active ? '' : ' inactive'}`}
        onClick={() => onToggleExpand(lugar.id)}
        style={{ cursor: 'pointer' }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-mute)" strokeWidth="2"
          style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6"/>
        </svg>
        <span className="vlist-name">{lugar.name}</span>
        <span className="vlist-meta">{metaParts.join(' · ')}</span>
        {isAdmin && (
          <div className="vlist-actions" onClick={e => e.stopPropagation()}>
            <button className="btn btn-icon btn-ghost" title="Editar" onClick={() => onEdit(lugar)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button className="btn btn-icon btn-ghost" title={lugar.active ? 'Desactivar' : 'Activar'} onClick={() => onToggleActive(lugar)}>
              {lugar.active ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
      {expanded && (
        <div style={{ padding: '4px 16px 14px 46px', background: 'var(--surface2)' }}>
          {lugar.espacios?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {lugar.espacios.map(esp => (
                <span
                  key={esp.id}
                  draggable={isAdmin}
                  onDragStart={() => { draggedId.current = esp.id; }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); reorderEspacios(esp.id); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 12, padding: '3px 8px', borderRadius: 5,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    cursor: isAdmin ? 'grab' : 'default',
                  }}
                >
                  {esp.name}
                  {isAdmin && (
                    <button onClick={() => removeEspacio(esp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, fontSize: 11 }}>✕</button>
                  )}
                </span>
              ))}
            </div>
          )}
          {isAdmin && <EspacioAdder lugarId={lugar.id} onAdded={addEspacio} />}
        </div>
      )}
    </div>
  );
}

export default function LugaresPage() {
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'ventas.lugares');

  const [lugares, setLugares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lugares${showInactive ? '?all=1' : ''}`);
      setLugares(Array.isArray(data) ? data : []);
    } catch {
      setLugares([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = lugares.filter(l => {
    const q = normalize(search);
    return !q || normalize(l.name).includes(q) || normalize(l.address).includes(q);
  });

  const handleSaved = saved => {
    setLugares(prev => {
      const idx = prev.findIndex(l => l.id === saved.id);
      const withEspacios = { espacios: prev[idx]?.espacios || [], ...saved };
      const next = idx >= 0 ? prev.map((l, i) => i === idx ? withEspacios : l) : [...prev, withEspacios];
      return next.sort((a, b) => a.name.localeCompare(b.name, 'es'));
    });
    setShowModal(false);
    setEditing(null);
  };

  const handleToggleActive = async lugar => {
    if (lugar.active && !window.confirm(`¿Desactivar "${lugar.name}"? Ya no va a aparecer para elegir en cotizaciones.`)) return;
    try {
      const { data } = await api.put(`/lugares/${lugar.id}`, { name: lugar.name, address: lugar.address, active: !lugar.active });
      handleSaved(data);
    } catch {}
  };

  const handleEspaciosChange = (lugarId, espacios) => {
    setLugares(prev => prev.map(l => l.id === lugarId ? { ...l, espacios } : l));
  };

  const handleImportFile = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const rows = parseLugaresCsv(String(reader.result));
        if (rows.length === 0) { alert('No se encontraron filas válidas en el archivo'); return; }
        const { data } = await api.post('/lugares/import', { rows });
        alert(`Importados: ${data.imported} · Ya existían: ${data.skipped} · Total en archivo: ${data.total}`);
        load();
      } catch (err) {
        alert(err.response?.data?.error || 'Error al importar el archivo');
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Lugares</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
              <button className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                {importing ? 'Importando…' : 'Importar CSV'}
              </button>
              <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
                + Nuevo
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
          placeholder="Buscar por nombre o dirección…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Mostrar inactivos
          </label>
        )}
        <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          {filtered.length} {filtered.length === 1 ? 'lugar' : 'lugares'}
        </span>
      </div>

      <div className="vlist">
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-dim)' }}>
            {search ? 'Sin resultados.' : 'No hay lugares registrados.'}
          </div>
        ) : (
          filtered.map(l => (
            <LugarRow
              key={l.id}
              lugar={l}
              isAdmin={isAdmin}
              expanded={expandedId === l.id}
              onToggleExpand={id => setExpandedId(prev => prev === id ? null : id)}
              onEdit={lg => { setEditing(lg); setShowModal(true); }}
              onToggleActive={handleToggleActive}
              onEspaciosChange={handleEspaciosChange}
            />
          ))
        )}
      </div>

      {showModal && (
        <LugarModal
          lugar={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
